import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function FilterModal({
  show,
  onHide,
  applyFilters,
  removeFilters,
  apidata,
}) {
  const [filters, setFilters] = useState({
    vehicle: "",
    assigned_to: "",
    status: "",
    category: "",
    pendingissue: "",
    ticket_resolved_month: "",
    ticket_issued_month: "",
  });

  const [vehicles, setVehicles] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pendingIssues, setPendingIssues] = useState([]);

  useEffect(() => {
    if (!apidata || !Array.isArray(apidata)) return;

    // Unique Vehicles
    const uniqueVehicles = [
      ...new Set(apidata.map((item) => item.vehicle_number).filter(Boolean)),
    ];

    // Unique Assigned Users
    const uniqueAssignedUsers = [
      ...new Set(apidata.map((item) => item.assigned_to?.name).filter(Boolean)),
    ];

    // Unique Statuses
    const uniqueStatuses = [
      ...new Set(apidata.map((item) => item.status).filter(Boolean)),
    ];

    const uniqueCategories = [
      ...new Set(apidata.map((item) => item.category?.name).filter(Boolean)),
    ];
    const uniquePendingIssues = [
      ...new Set(
        apidata.map((item) => item.pendingissue?.trim()).filter(Boolean),
      ),
    ];
    setCategories(uniqueCategories);
    setVehicles(uniqueVehicles);
    setAssignedUsers(uniqueAssignedUsers);
    setStatuses(uniqueStatuses);
    setPendingIssues(uniquePendingIssues);
  }, [apidata]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApply = () => {
    applyFilters(filters);
  };

  const handleRemove = () => {
    const emptyFilters = {
      vehicle: "",
      assigned_to: "",
      status: "",
      category: "",
      ticket_resolved_month: "",
      ticket_issued_month: "",
    };

    setFilters(emptyFilters);
    removeFilters();
  };

  const getDateFromMonth = (monthValue) => {
    if (!monthValue) return null;

    const [year, month] = monthValue.split("-");

    return new Date(Number(year), Number(month) - 1, 1);
  };

  const formatMonth = (date) => {
    if (!date) return "";

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  };

  // Convert vehicles into react-select options
  const vehicleOptions = vehicles.map((vehicle) => ({
    value: vehicle,
    label: vehicle,
  }));

  // Convert users into react-select options
  const assignedUserOptions = assignedUsers.map((user) => ({
    value: user,
    label: user,
  }));

  const pendingIssueOptions = pendingIssues.map((issue) => ({
    value: issue,
    label: issue,
  }));

  // Find currently selected vehicle
  const selectedVehicle =
    vehicleOptions.find((option) => option.value === filters.vehicle) || null;

  // Find currently selected user
  const selectedAssignedUser =
    assignedUserOptions.find(
      (option) => option.value === filters.assigned_to,
    ) || null;

  const selectedPendingIssue =
    pendingIssueOptions.find(
      (option) => option.value === filters.pendingissue,
    ) || null;

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Filter Options</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Vehicle */}
          <Form.Group controlId="vehicleFilter" className="mb-3">
            <Form.Label>Vehicle</Form.Label>

            <Select
              options={vehicleOptions}
              value={selectedVehicle}
              onChange={(selectedOption) => {
                setFilters((prev) => ({
                  ...prev,
                  vehicle: selectedOption ? selectedOption.value : "",
                }));
              }}
              placeholder="Search Vehicle..."
              isClearable
              isSearchable
              noOptionsMessage={() => "No vehicle found"}
            />
          </Form.Group>

          {/* Category */}
          <Form.Group controlId="categoryFilter" className="mb-3">
            <Form.Label>Category</Form.Label>

            <Form.Control
              as="select"
              name="category"
              value={filters.category}
              onChange={handleChange}
            >
              <option value="">All Categories</option>

              {categories.map((category, index) => (
                <option key={index} value={category}>
                  {category}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Assigned To */}
          <Form.Group controlId="assignedToFilter" className="mb-3">
            <Form.Label>Assigned To</Form.Label>

            <Select
              options={assignedUserOptions}
              value={selectedAssignedUser}
              onChange={(selectedOption) => {
                setFilters((prev) => ({
                  ...prev,
                  assigned_to: selectedOption ? selectedOption.value : "",
                }));
              }}
              placeholder="Search User..."
              isClearable
              isSearchable
              noOptionsMessage={() => "No user found"}
            />
          </Form.Group>

          {/* Status */}
          <Form.Group controlId="statusFilter" className="mb-3">
            <Form.Label>Status</Form.Label>

            <Form.Control
              as="select"
              name="status"
              value={filters.status}
              onChange={handleChange}
            >
              <option value="">All Status</option>

              {statuses.map((status, index) => (
                <option key={index} value={status}>
                  {status}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Pending Issue */}
          <Form.Group controlId="pendingIssueFilter" className="mb-3">
            <Form.Label>Pending Issue</Form.Label>

            <Select
              options={pendingIssueOptions}
              value={selectedPendingIssue}
              onChange={(selectedOption) => {
                setFilters((prev) => ({
                  ...prev,
                  pendingissue: selectedOption ? selectedOption.value : "",
                }));
              }}
              placeholder="Search Pending Issue..."
              isClearable
              isSearchable
              noOptionsMessage={() => "No pending issue found"}
            />
          </Form.Group>

          {/* Ticket Issued Month */}
          <Form.Group controlId="ticketIssuedMonth" className="mb-3">
            <Form.Label>Ticket Issued Month</Form.Label>
            <br />

            <DatePicker
              selected={getDateFromMonth(filters.ticket_issued_month)}
              onChange={(date) => {
                setFilters((prev) => ({
                  ...prev,
                  ticket_issued_month: formatMonth(date),
                }));
              }}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              showFullMonthYearPicker
              placeholderText="Select Issued Month"
              className="form-control"
              isClearable
            />
          </Form.Group>

          {/* Ticket Resolved Month */}
          <Form.Group controlId="ticketResolvedMonth" className="mb-3">
            <Form.Label>Ticket Resolved Month</Form.Label>
            <br />

            <DatePicker
              selected={getDateFromMonth(filters.ticket_resolved_month)}
              onChange={(date) => {
                setFilters((prev) => ({
                  ...prev,
                  ticket_resolved_month: formatMonth(date),
                }));
              }}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              showFullMonthYearPicker
              placeholderText="Select Resolved Month"
              className="form-control"
              isClearable
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleRemove}>
          Remove Filters
        </Button>

        <Button variant="primary" onClick={handleApply}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
